import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Upload, CheckCircle, BarChart3, ArrowRight, ArrowLeft,
  Sparkles, FileSearch, BookOpen, X, Rocket,
} from 'lucide-react';
import { Button } from './ui/button';
import api from '../lib/api';

const ROLE_GREETING = {
  owner: "As the account owner, you have full control over your organization's financials.",
  admin: "As an admin, you can manage your team and all financial data.",
  accountant: "As an accountant, you can upload statements, manage transactions, and generate reports.",
  viewer: "As a viewer, you can access reports and monitor your organization's financial health.",
};

const STEPS = [
  {
    id: 'welcome',
    icon: Sparkles,
    iconBg: 'bg-gradient-to-br from-[#D94E18] to-[#E8723A]',
    title: (name) => `Welcome, ${name}!`,
    subtitle: (role) => ROLE_GREETING[role] || ROLE_GREETING.viewer,
    body: "Let's take a quick tour so you know where everything is. This takes about 30 seconds.",
  },
  {
    id: 'upload',
    icon: Upload,
    iconBg: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
    title: () => 'Upload bank statements',
    subtitle: () => 'PDF, CSV, or photos — the AI handles the rest.',
    body: "Head to the Upload page to drop in your bank statements. Kaya will extract every transaction, detect the currency, and classify each line automatically.",
    tip: 'You can even snap a photo of a paper statement on your phone.',
    route: '/upload',
    cta: 'Go to Upload',
    roles: ['owner', 'admin', 'accountant'],
  },
  {
    id: 'review',
    icon: FileSearch,
    iconBg: 'bg-gradient-to-br from-blue-500 to-blue-600',
    title: () => 'Review AI suggestions',
    subtitle: () => 'Accept, edit, or reject — you stay in control.',
    body: "After upload, transactions land in the Review queue. The AI suggests an account and category for each one. Accept with one tap, or correct it — the AI learns from every correction you make.",
    tip: 'The more you correct, the smarter Kaya becomes.',
    route: '/transactions',
    cta: 'Go to Review',
    roles: ['owner', 'admin', 'accountant'],
  },
  {
    id: 'journal',
    icon: BookOpen,
    iconBg: 'bg-gradient-to-br from-violet-500 to-violet-600',
    title: () => 'Journal entries & accounts',
    subtitle: () => 'Double-entry bookkeeping, simplified.',
    body: "Approved transactions become journal entries automatically. You can also create manual entries for adjustments, accruals, or recurring charges. Your Chart of Accounts is pre-seeded for your country.",
    tip: 'Set up recurring entries for rent, salaries, etc.',
    route: '/journal',
    cta: 'View Journal',
    roles: ['owner', 'admin', 'accountant'],
  },
  {
    id: 'reports',
    icon: BarChart3,
    iconBg: 'bg-gradient-to-br from-amber-500 to-amber-600',
    title: () => 'Real-time reports',
    subtitle: () => 'P&L, Balance Sheet, VAT — always up to date.',
    body: "Navigate to Reporting for live financial statements. Everything updates in real time as transactions are posted. Export to PDF or drill down into individual line items.",
    route: '/reports',
    cta: 'View Reports',
  },
  {
    id: 'done',
    icon: Rocket,
    iconBg: 'bg-gradient-to-br from-[#D94E18] to-[#E8723A]',
    title: (name) => `You're all set, ${name}!`,
    subtitle: () => 'Start by uploading your first bank statement.',
    body: "That's the essentials. You can always reach the AI assistant via the chat bubble in the bottom-right corner if you need help along the way.",
  },
];

export default function WelcomeWizard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (!user) return;
    const key = `kaya_wizard_done_${user.id}`;
    if (localStorage.getItem(key)) return;
    // Check backend flag
    api.get('/auth/me').then(res => {
      if (!res.data.onboarding_wizard_completed) {
        setVisible(true);
      } else {
        localStorage.setItem(key, '1');
      }
    }).catch(() => {});
  }, [user]);

  if (!visible || !user) return null;

  const role = user.role || 'viewer';
  const firstName = (user.name || 'there').split(' ')[0];

  // Filter steps by role
  const filteredSteps = STEPS.filter(s => !s.roles || s.roles.includes(role));
  const current = filteredSteps[step];
  const isLast = step === filteredSteps.length - 1;
  const isFirst = step === 0;
  const Icon = current.icon;

  const dismiss = async () => {
    setExiting(true);
    try { await api.post('/auth/complete-wizard'); } catch {}
    localStorage.setItem(`kaya_wizard_done_${user.id}`, '1');
    setTimeout(() => setVisible(false), 300);
  };

  const goNext = () => {
    if (isLast) {
      dismiss();
    } else {
      setStep(s => s + 1);
    }
  };

  const goPrev = () => {
    if (!isFirst) setStep(s => s - 1);
  };

  const handleCta = () => {
    if (current.route) {
      dismiss();
      navigate(current.route);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[60] flex items-center justify-center p-4 transition-opacity duration-300 ${exiting ? 'opacity-0' : 'opacity-100'}`}
      data-testid="welcome-wizard"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={dismiss} />

      {/* Card */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
        {/* Close */}
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center z-10 transition-colors"
          data-testid="wizard-close"
        >
          <X className="w-4 h-4 text-zinc-500" />
        </button>

        {/* Icon header */}
        <div className="pt-8 pb-2 flex justify-center">
          <div className={`w-16 h-16 ${current.iconBg} rounded-2xl flex items-center justify-center shadow-lg`}>
            <Icon className="w-8 h-8 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="px-8 pb-2 pt-4 text-center">
          <h2
            className="text-xl font-bold text-zinc-900 mb-1"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="wizard-title"
          >
            {current.title(firstName)}
          </h2>
          <p className="text-sm text-[#D94E18] font-medium mb-4">
            {current.subtitle(role)}
          </p>
          <p className="text-sm text-zinc-600 leading-relaxed mb-3">
            {current.body}
          </p>
          {current.tip && (
            <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-2.5 text-left mb-2">
              <p className="text-xs text-amber-800">
                <span className="font-semibold">Tip:</span> {current.tip}
              </p>
            </div>
          )}
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 py-3">
          {filteredSteps.map((_, i) => (
            <div
              key={`dot-${i}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === step ? 'w-6 bg-[#D94E18]' : i < step ? 'w-1.5 bg-[#D94E18]/40' : 'w-1.5 bg-zinc-200'
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="px-8 pb-6 flex items-center gap-3">
          {!isFirst && (
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              className="flex-shrink-0"
              data-testid="wizard-prev"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}

          <div className="flex-1" />

          {current.cta && !isLast && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCta}
              className="text-[#D94E18] border-[#D94E18]/30 hover:bg-[#D94E18]/5"
              data-testid="wizard-cta"
            >
              {current.cta}
            </Button>
          )}

          <Button
            size="sm"
            onClick={goNext}
            className="bg-[#D94E18] hover:bg-[#C2410C] text-white flex-shrink-0"
            data-testid="wizard-next"
          >
            {isLast ? (
              <>Let's go!</>
            ) : (
              <>Next <ArrowRight className="w-4 h-4 ml-1" /></>
            )}
          </Button>
        </div>

        {/* Skip link */}
        {!isLast && (
          <div className="text-center pb-4">
            <button
              onClick={dismiss}
              className="text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
              data-testid="wizard-skip"
            >
              Skip tour
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
